// The undo history: ONE stack of steps, and the life of the step under way.
//
// A step is the drawing as it was before a change (a snapshot) on the undo stack;
// undo swaps it with the drawing, onto the redo stack. A gesture — a focused
// panel field, a drag, a grip's or a handle's press — OPENS a step where it
// begins, and how it ends decides whether the step is recorded. Every rule of a
// step's life is here, and nowhere else:
//  - `push` records a step before a change: from where the open step began
//    (taking it over: a field's focus and its change handler's push are ONE
//    step, not two), else from now.
//  - `end('keep')` records the open step only if the drawing changed since it
//    began; `end('cancel')` puts the drawing back there and records nothing;
//    `end('drop')` records and restores nothing.
//  - A start held by its caller (`from`: a mark from `begin` or `mark`) says
//    where the step began instead. A step opened by `begin` that a push has
//    taken over since is recorded already: keeping it records nothing more.
//  - A start taken on a drawing since replaced (a load, an undo) is stale: a push
//    starts from now instead, an end records and restores nothing.
//  - A recorded step clears redo; the stack keeps the last `UNDO_MAX`.
//  - A foreign entry (the title block, which lives outside the drawing) shares
//    the stack: undo hands it to the host, which puts that thing back.
// DOM-free: main.ts injects the drawing and its bookkeeping as an `UndoHost`
// (the unit tests, an in-memory one). A snapshot is whatever the host makes of
// the drawing (`S`: main.ts's holds the imported plan by reference,
// drawingSnapshot.ts); the history only keeps them and asks the host whether
// two are the same.

export const UNDO_MAX = 200;

/** How a step under way ends: recorded if it changed anything, put back, or let go. */
export type StepEnd = 'keep' | 'cancel' | 'drop';

/** Where a step began: the drawing then, the drawing object it was taken on, and
 *  whether it opened the step (`begin`) or only marked the drawing (`mark`).
 *  Callers hold it and hand it back; they never read it. */
export interface UndoMark<S = string> {
  readonly snap: S;
  readonly drawing: object;
  readonly opened: boolean;
}

/** An entry of either stack: a drawing snapshot, or a foreign thing's entry. */
export type UndoEntry<F, S = string> = { readonly snap: S } | { readonly foreign: F };

/** Both stacks, set aside (the product editor's throwaway drawing has its own run). */
export interface UndoStash<F, S = string> {
  readonly undo: readonly UndoEntry<F, S>[];
  readonly redo: readonly UndoEntry<F, S>[];
}

/** What the history needs from the app. */
export interface UndoHost<F, S = string> {
  /** The drawing now, as a snapshot. */
  snapshot(): S;
  /** Whether two snapshots are of the same drawing (absent: `===`). */
  same?(a: S, b: S): boolean;
  /** The live drawing object: a load or a restore replaces it. */
  drawing(): object;
  /** Put the drawing back as a snapshot has it. */
  restore(snap: S): void;
  /** Put a foreign entry's thing back as the entry has it; return that thing's
   *  entry as it was, for the other stack (null: there is none). */
  swapForeign(entry: F): F | null;
  /** A drawing step was recorded (the drawing changes, or just has). */
  recorded(): void;
  /** The step under way closed, or a new one opened: per-gesture state ends. */
  gestureEnded(): void;
  /** What undo and redo can do may have changed. */
  stacksChanged(): void;
}

export interface UndoHistory<F, S = string> {
  /** Open a step at the drawing now (a gesture begins); its mark. */
  begin(): UndoMark<S>;
  /** Whether a step is open. */
  isOpen(): boolean;
  /** Mark the drawing now without opening a step: where a step will have begun. */
  mark(): UndoMark<S>;
  /** Whether a mark was taken on the drawing that is live now. */
  live(m: UndoMark<S>): boolean;
  /** Record a step before a change: from `from`, else the open step's start
   *  (taking it over), else now. Closes the open step. */
  push(from?: UndoMark<S>): void;
  /** Close the step that began at `from` (else the open one): see `StepEnd`. */
  end(how: StepEnd, from?: UndoMark<S>): void;
  /** Record a foreign entry on the same stack (the drawing is untouched). */
  pushForeign(entry: F): void;
  /** Step one entry back / forward; false when there was none. */
  undo(): boolean;
  redo(): boolean;
  canUndo(): boolean;
  canRedo(): boolean;
  /** Both stacks as they are now, to be put back by `unstash`. */
  stash(): UndoStash<F, S>;
  unstash(s: UndoStash<F, S>): void;
}

export function createUndoHistory<F, S = string>(host: UndoHost<F, S>, max = UNDO_MAX): UndoHistory<F, S> {
  let undo: UndoEntry<F, S>[] = [];
  let redo: UndoEntry<F, S>[] = [];
  let open: UndoMark<S> | null = null;
  const same = host.same ?? ((a: S, b: S) => a === b);

  const markNow = (opened: boolean): UndoMark<S> => ({ snap: host.snapshot(), drawing: host.drawing(), opened });
  const live = (m: UndoMark<S>): boolean => m.drawing === host.drawing();
  const record = (entry: UndoEntry<F, S>): void => {
    undo.push(entry);
    if (undo.length > max) undo.shift();
    redo = [];
  };
  const close = (): void => {
    open = null;
    host.gestureEnded();
  };
  const step = (from: UndoEntry<F, S>[], to: UndoEntry<F, S>[]): boolean => {
    const entry = from.pop();
    if (!entry) return false;
    if ('foreign' in entry) {
      const was = host.swapForeign(entry.foreign);
      if (was != null) to.push({ foreign: was });
    } else {
      to.push({ snap: host.snapshot() });
      host.restore(entry.snap);
    }
    host.stacksChanged();
    return true;
  };

  return {
    begin() {
      open = markNow(true);
      host.gestureEnded();
      return open;
    },
    isOpen: () => open !== null,
    mark: () => markNow(false),
    live,
    push(from) {
      const start = from ?? open;
      const snap = start && live(start) ? start.snap : host.snapshot();
      host.recorded();
      record({ snap });
      close();
      host.stacksChanged();
    },
    end(how, from) {
      const start = from ?? open;
      const taken = from?.opened === true && open === null;
      close();
      if (!start || taken || how === 'drop' || !live(start) || same(host.snapshot(), start.snap)) return;
      if (how === 'cancel') { host.restore(start.snap); return; }
      host.recorded();
      record({ snap: start.snap });
      host.stacksChanged();
    },
    pushForeign(entry) {
      record({ foreign: entry });
      host.stacksChanged();
    },
    undo: () => step(undo, redo),
    redo: () => step(redo, undo),
    canUndo: () => undo.length > 0,
    canRedo: () => redo.length > 0,
    stash: () => ({ undo: undo.slice(), redo: redo.slice() }),
    unstash(s) {
      undo = s.undo.slice();
      redo = s.redo.slice();
      host.stacksChanged();
    },
  };
}
